import type { QueryClient } from '@tanstack/react-query'
import { columnsQueryKeys } from '@features/columns/api/queryKeys'
import { permissionsQueryKeys } from '@features/permissions/api/queryKeys'
import { recordsQueryKeys } from '@features/records/api/queryKeys'
import { searchQueryKeys } from '@features/search/api/queryKeys'
import { sidebarQueryKeys } from '@features/sidebar/api/queryKeys'
import { tablesQueryKeys } from '../api/queryKeys'

// Every search that finds tables or fields: the sidebar's, Cmd-K's (its
// results and the preview it opens on) and the formula editor's suggestions.
const TABLE_SEARCH_KEYS: readonly (readonly unknown[])[] = [
  ['sidebar', 'search'],
  ['search'],
  ['search-global'],
  searchQueryKeys.searchPreviewAll,
  ['search-global-pages'],
  ['search-global-window'],
  ['search-preview-records'],
  columnsQueryKeys.formulaFieldSearchAll,
]

// Every query whose answer names, lists or describes tables and their fields.
const TABLE_METADATA_KEYS: readonly (readonly unknown[])[] = [
  // The table list, capabilities and every table's fields.
  tablesQueryKeys.tables,
  ['views'],
  recordsQueryKeys.recordsAll,
  sidebarQueryKeys.tableOrganization,
  sidebarQueryKeys.favorites,
  sidebarQueryKeys.hidden,
  sidebarQueryKeys.recent,
  ...TABLE_SEARCH_KEYS,
  permissionsQueryKeys.permissionsTables,
  permissionsQueryKeys.resourcePermissionEditors,
]

/**
 * After a table is created where its lists are already refreshed: the
 * searches keep their own copies, so Cmd-K and the sidebar would miss it.
 */
export async function invalidateTableSearches(queryClient: QueryClient): Promise<void> {
  await Promise.all(
    TABLE_SEARCH_KEYS.map((queryKey) => queryClient.invalidateQueries({ queryKey }))
  )
}

/**
 * After a change that can add, rename or remove tables or fields away from
 * the grid that shows them (an integration save, deleting a table), so no
 * list, search or sidebar keeps showing one that's gone. Queries on screen
 * refetch now; the rest when they're next used.
 */
export async function invalidateTableMetadata(queryClient: QueryClient): Promise<void> {
  await Promise.all(
    TABLE_METADATA_KEYS.map((queryKey) => queryClient.invalidateQueries({ queryKey }))
  )
}
